import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import {
  getEspLimitsRequest,
  updateInboxPlacementMaxSendingLimitRequest,
  updateMaxSendingLimitRequest,
  updateSendingLimitsRequest,
} from './extra-actions';
import { EspLimits } from './types';

interface RequestState {
  status: RequestStatus;
  message: string;
  error: any;
}

interface State {
  espLimits: EspLimits[];
  inboxAnalyzerEsps: EspLimits[];
  getEspLimitRequest: RequestState;
  updateMaxSendingLimitRequest: RequestState;
  updateInboxPlacementMaxSendingLimitRequest: RequestState;
  updateSendingLimitsRequest: RequestState;
  isLoading: boolean;
}

const initialState: State = {
  getEspLimitRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateMaxSendingLimitRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateInboxPlacementMaxSendingLimitRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  updateSendingLimitsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  espLimits: [],
  inboxAnalyzerEsps: [],
  isLoading: false,
};

const EmailDailySendingLimitSlice = createSlice({
  name: 'emailDailySendingLimit',
  initialState,
  reducers: {
    resetEmailDailySendingLimitState: (state) => {
      state.espLimits = initialState.espLimits;
      state.inboxAnalyzerEsps = initialState.inboxAnalyzerEsps;
      state.updateMaxSendingLimitRequest =
        initialState.updateMaxSendingLimitRequest;
      state.updateSendingLimitsRequest = initialState.updateSendingLimitsRequest;
      state.getEspLimitRequest = initialState.getEspLimitRequest;
    },
    resetUpdateMaxSendingLimitRequestState: (state) => {
      state.updateMaxSendingLimitRequest =
        initialState.updateMaxSendingLimitRequest;
    },
    resetUpdateInboxPlacementMaxSendingLimitRequestState: (state) => {
      state.updateInboxPlacementMaxSendingLimitRequest =
        initialState.updateInboxPlacementMaxSendingLimitRequest;
    },
    resetUpdateSendingLimitsRequestState: (state) => {
      state.updateSendingLimitsRequest = initialState.updateSendingLimitsRequest;
    },
  },
  extraReducers: (builder) => {
    //   Get esp sending limits
    builder.addCase(getEspLimitsRequest.pending, (state) => {
      state.getEspLimitRequest.status = RequestStatus.Pending;
      state.getEspLimitRequest.error = null;
      state.isLoading = true;
    });

    builder.addCase(getEspLimitsRequest.fulfilled, (state, action) => {
      state.getEspLimitRequest.status = RequestStatus.Succeeded;
      state.getEspLimitRequest.error = null;
      state.espLimits = action.payload.payload?.esps;
      state.inboxAnalyzerEsps = action.payload.payload?.inboxAnalyzerEsps;
      state.isLoading = false;
    });

    builder.addCase(getEspLimitsRequest.rejected, (state, action) => {
      state.getEspLimitRequest.status = RequestStatus.Failed;
      state.getEspLimitRequest.error = action.payload;
      state.isLoading = false;
    });

    //   Update max sending limit
    builder.addCase(updateMaxSendingLimitRequest.pending, (state) => {
      state.updateMaxSendingLimitRequest.status = RequestStatus.Pending;
      state.updateMaxSendingLimitRequest.error = null;
      state.isLoading = true;
    });

    builder.addCase(updateMaxSendingLimitRequest.fulfilled, (state) => {
      state.updateMaxSendingLimitRequest.status = RequestStatus.Succeeded;
      state.updateMaxSendingLimitRequest.error = null;
      state.isLoading = false;
    });

    builder.addCase(updateMaxSendingLimitRequest.rejected, (state, action) => {
      state.updateMaxSendingLimitRequest.status = RequestStatus.Failed;
      state.updateMaxSendingLimitRequest.error = action.payload;
      state.isLoading = false;
    });

    //   Update inbox max sending limit
    builder.addCase(
      updateInboxPlacementMaxSendingLimitRequest.pending,
      (state) => {
        state.updateInboxPlacementMaxSendingLimitRequest.status =
          RequestStatus.Pending;
        state.updateInboxPlacementMaxSendingLimitRequest.error = null;
        state.isLoading = true;
      },
    );

    builder.addCase(
      updateInboxPlacementMaxSendingLimitRequest.fulfilled,
      (state) => {
        state.updateInboxPlacementMaxSendingLimitRequest.status =
          RequestStatus.Succeeded;
        state.updateInboxPlacementMaxSendingLimitRequest.error = null;
        state.isLoading = false;
      },
    );

    builder.addCase(
      updateInboxPlacementMaxSendingLimitRequest.rejected,
      (state, action) => {
        state.updateInboxPlacementMaxSendingLimitRequest.status =
          RequestStatus.Failed;
        state.updateInboxPlacementMaxSendingLimitRequest.error = action.payload;
        state.isLoading = false;
      },
    );

    // Update sending limits (unified)
    builder.addCase(updateSendingLimitsRequest.pending, (state) => {
      state.updateSendingLimitsRequest.status = RequestStatus.Pending;
      state.updateSendingLimitsRequest.error = null;
      state.isLoading = true;
    });

    builder.addCase(updateSendingLimitsRequest.fulfilled, (state) => {
      state.updateSendingLimitsRequest.status = RequestStatus.Succeeded;
      state.updateSendingLimitsRequest.error = null;
      state.isLoading = false;
    });

    builder.addCase(updateSendingLimitsRequest.rejected, (state, action) => {
      state.updateSendingLimitsRequest.status = RequestStatus.Failed;
      state.updateSendingLimitsRequest.error = action.payload;
      state.isLoading = false;
    });
  },
});

export const {
  resetEmailDailySendingLimitState,
  resetUpdateMaxSendingLimitRequestState,
  resetUpdateInboxPlacementMaxSendingLimitRequestState,
  resetUpdateSendingLimitsRequestState,
} = EmailDailySendingLimitSlice.actions;
export default EmailDailySendingLimitSlice.reducer;
